import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { NGXLogger } from "ngx-logger";
import { of } from "rxjs";
import {
  catchError,
  concatMap,
  exhaustMap,
  map,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { AuthService } from "../../auth/auth.service";
import { LocalStorageService } from "../../local-storage/local-storage.service";
import { ToastService } from "../../shared/toast.service";
import { PadlockEventsService } from "../padlock-events.service";
import { PadlockService } from "../padlock.service";
import {
  GetPadlockTokenFailure,
  GetPadlockTokenSuccess,
  PadlockTokenActions,
  PadlockTokenActionTypes,
  ProvisionUserFailure,
  ProvisionUserSuccess,
} from "./padlock-token.actions";
import { padlockTokenFeatureKey } from "./padlock-token.reducer";

@Injectable()
export class PadlockTokenEffects {
  getPadlockToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PadlockTokenActionTypes.GetPadlockToken),
      exhaustMap((action) =>
        this.padlock.login(action.email, action.password).pipe(
          map((token: { token: string; type: string; expires: number }) => {
            this.logger.debug(
              `SIDE EFFECT: ${PadlockTokenActionTypes.GetPadlockTokenSuccess}`,
            );
            return new GetPadlockTokenSuccess(token.token, "credential");
          }),
          catchError(() => {
            this.logger.warn(
              `SIDE EFFECT: ${PadlockTokenActionTypes.GetPadlockTokenFailure}`,
            );
            return of(new GetPadlockTokenFailure(null, "credential"));
          }),
        ),
      ),
    ),
  );

  getPadlockTokenFromB2CToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PadlockTokenActionTypes.GetPadlockTokenFromB2CToken),
      concatMap((action) =>
        this.padlock.loginB2C(action.b2cToken).pipe(
          map(
            (token: { token: string; type: string; expires: number }) =>
              new GetPadlockTokenSuccess(token.token, "b2c"),
          ),
          catchError((error) => {
            this.logger.warn(
              `SIDE EFFECT: ${PadlockTokenActionTypes.GetPadlockTokenFailure}`,
            );
            return of(new GetPadlockTokenFailure(error, "b2c"));
          }),
        ),
      ),
    ),
  );

  getPadlockTokenSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PadlockTokenActionTypes.GetPadlockTokenSuccess),
        withLatestFrom(this.store),
        tap(([, state]) => {
          const stateSnapshot = state[padlockTokenFeatureKey];
          this.logger.debug(`SIDE EFFECT: persist ${padlockTokenFeatureKey}`);

          this.localStorage.persist(padlockTokenFeatureKey, stateSnapshot);
          this.auth.validateToken();
        }),
      ),
    { dispatch: false },
  );

  provisionUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PadlockTokenActionTypes.ProvisionUser),
      concatMap((action) =>
        this.padlock
          .provisionUser(action.email, action.password, action.workspaceIDs)
          .pipe(
            map(() => new ProvisionUserSuccess(action.email)),
            catchError((error) =>
              of(new ProvisionUserFailure(action.email, error)),
            ),
          ),
      ),
    ),
  );

  provisionUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PadlockTokenActionTypes.ProvisionUserSuccess),
        tap((action) => {
          // Toast
          this.toast.openToast(`${action.email} provisioned successfully!`);

          // Raise an event for any components to use
          this.events.userProvisioned$.next(undefined);
        }),
      ),
    { dispatch: false },
  );

  provisionUserFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PadlockTokenActionTypes.ProvisionUserFailure),
        tap((action) => {
          // Toast
          let message = `Error provisioning ${action.email}`;
          if (
            action.error.error?.message ==
            'duplicate key value violates unique constraint "users_email_key"'
          ) {
            message += " (user already exists)";
          } else if (action.error.statusText) {
            message += ` (${action.error.statusText})`;
          }
          this.toast.openErrorToast(message);
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions<PadlockTokenActions>,
    private store: Store<any>,
    private padlock: PadlockService,
    private localStorage: LocalStorageService,
    private logger: NGXLogger,
    private auth: AuthService,
    private toast: ToastService,
    private events: PadlockEventsService,
  ) {}
}
